import { useCallback } from 'react';

export default function HeroSection() {
  const handleScrollTo = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20atmosphere%20with%20warm%20golden%20accents%20and%20deep%20navy%20shadows%2C%20classic%20film%20strip%20elements%20subtly%20blended%2C%20nostalgic%20yet%20modern%20ambiance%2C%20soft%20dramatic%20lighting%2C%20no%20text%2C%20abstract%20cinematic%20composition%20with%20rich%20texture%20and%20depth%2C%20elegant%20and%20immersive%20mood&width=1800&height=900&seq=hero-bg-cinema-90&orientation=landscape"
          alt="90影院 — 高清影视背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-12 py-16 md:py-20">
          <div className="flex-1 w-full text-center lg:text-left">
            <p className="inline-block px-4 py-1.5 rounded-full bg-primary-500/20 border border-primary-400/30 text-primary-300 text-sm font-medium mb-4">
              经典重温 · 新片速递 · 每日更新
            </p>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              90影院
              <span className="block text-primary-400 mt-1">8090后的高清影视殿堂</span>
            </h1>

            <p className="mt-3 text-lg md:text-xl text-primary-200/80 font-medium">
              高清 / 超清 / 4K / 蓝光 — 免费在线观看，播放不缓冲
            </p>

            <p className="mt-5 text-sm md:text-base text-foreground-200 leading-relaxed max-w-xl mx-auto lg:mx-0">
              90影院专注为8090后影视爱好者打造一站式观影平台，涵盖电影、电视剧、综艺、动漫、纪录片等全品类资源。
              每日更新最新院线大片与经典回顾，支持高清、超清、4K、蓝光多画质切换，播放流畅不卡顿，告别缓冲等待。
              无论怀旧重温还是追赶新番，这里都有你的专属片单。
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 md:gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-white">
                <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                <span className="text-sm text-foreground-200">日更<span className="text-white font-semibold ml-1">30+</span> 部</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                <span className="text-sm text-foreground-200">覆盖<span className="text-white font-semibold ml-1">5</span> 大品类</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                <span className="text-sm text-foreground-200">最高<span className="text-white font-semibold ml-1">4K蓝光</span> 画质</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="#cards"
                onClick={(e) => handleScrollTo(e, '#cards')}
                className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-foreground-950 font-semibold text-sm rounded-lg transition-all duration-200"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始观影
              </a>
              <a
                href="#updates"
                onClick={(e) => handleScrollTo(e, '#updates')}
                className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 border border-white/30 hover:border-white/60 text-white font-medium text-sm rounded-lg transition-all duration-200"
              >
                <i className="ri-notification-3-line text-lg"></i>
                查看最新更新
              </a>
            </div>
          </div>

          <div className="flex-1 w-full max-w-[520px] lg:max-w-none">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary-500/10 rounded-2xl blur-2xl"></div>
              <div className="relative rounded-xl overflow-hidden border border-white/10">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxurious%20home%20theater%20setup%20with%20large%20screen%20displaying%20abstract%20cinematic%20visuals%2C%20warm%20ambient%20golden%20lighting%2C%20comfortable%20seating%2C%20modern%20minimalist%20interior%2C%20film%20posters%20collection%20on%20wall%2C%20cozy%20atmosphere%2C%20high%20quality%20cinematic%20photography%20style&width=800&height=560&seq=hero-visual-cinema-90&orientation=landscape"
                  alt="90影院 — 沉浸式观影体验"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-background-800/90 backdrop-blur-sm border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-primary-500/30 border-2 border-background-800 flex items-center justify-center">
                      <i className="ri-user-line text-xs text-primary-300"></i>
                    </div>
                  ))}
                </div>
                <span className="text-xs text-foreground-200 whitespace-nowrap">
                  <span className="text-primary-400 font-semibold">12.8万+</span> 在线观影
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}